'use client';

import React from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useShallow } from 'zustand/react/shallow';

import { useBasketStore, useProductGroupsByCode, useProfile } from '@/hooks';
import useCheckAccess from '@/hooks/useCheckAccess';
import { setCookie } from '@/utils';

const REFERRAL_COOKIE_MAX_AGE_SECONDS = 30 * 60;
const SIGN_UP_TYPES = ['agent', 'customer'];

const SyncWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const nextSearchParams = new URLSearchParams(searchParams.toString());
  const { data: profile } = useProfile();
  const [products, userType, add, remove] = useBasketStore(
    useShallow((state) => [
      state.products,
      state.userType,
      state.add,
      state.remove,
    ])
  );

  useCheckAccess(profile);

  const basketProductIds =
    searchParams.get('basket_product_ids') &&
    searchParams.get('basket_product_ids') !== ''
      ? searchParams
          .get('basket_product_ids')
          ?.split(',')
          .map((id) => Number(id))
      : [];

  const { refetch: fetchUrlProducts } = useProductGroupsByCode({
    codes: basketProductIds || [],
  });

  const handleQueryStrings = async () => {
    const userId = searchParams.get('user_id') || '';
    const signUpType = searchParams.get('type') || '';
    const hasSignUpType = SIGN_UP_TYPES.includes(signUpType);

    if (userId) {
      await setCookie({
        name: 'user_id',
        value: userId,
        maxAge: REFERRAL_COOKIE_MAX_AGE_SECONDS,
      });
      nextSearchParams.delete('user_id');
    }
    if (hasSignUpType) {
      await setCookie({
        name: 'sign_up_type',
        value: signUpType,
        maxAge: REFERRAL_COOKIE_MAX_AGE_SECONDS,
      });
      nextSearchParams.delete('type');
    }
    if (basketProductIds && basketProductIds.length > 0) {
      const result = await fetchUrlProducts();
      result.data?.list?.map((product) => {
        add({ product, count: 1 });
      });
      nextSearchParams.delete('basket_product_ids');
    }
    if (
      (basketProductIds && basketProductIds.length > 0) ||
      userId ||
      hasSignUpType
    ) {
      const nextSearch = nextSearchParams.toString();
      router.replace(nextSearch ? `${pathname}?${nextSearch}` : pathname);
    }
  };

  React.useEffect(() => {
    void handleQueryStrings();
  }, []);

  const { refetch: fetchNewProducts } = useProductGroupsByCode({
    codes: products.map((product) => Number(product.code)),
  });

  const [previousUserType, setPreviousUserType] = React.useState(userType);

  React.useEffect(() => {
    if (profile) {
      useBasketStore.setState({ userType: profile.user_type });
    } else {
      useBasketStore.setState({ userType: null });
    }
  }, [profile]);

  React.useEffect(() => {
    if (previousUserType !== userType && products.length > 0) {
      void fetchNewProducts().then((result) =>
        result.data?.list?.map((product) => {
          const quantity = products.find(
            (item) => item.code === product.code
          )?.quantity;
          remove({ code: product.code });
          add({ product, count: quantity });
        })
      );
    }
    setPreviousUserType(userType);
  }, [userType]);

  return <>{children}</>;
};

export default SyncWrapper;
